import {
  UnsavedChangesPrompt,
  PageHeader,
  CapabilityNav,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { Mode } from "../RouterSwitch";
import { useHistory, useParams } from "react-router-dom";
import VerticalTabs from "../../sharedComponents/VerticalTabs";
import { useForm } from "react-hook-form";
import { EhsRoleFormFields } from "./EhsRoleTypes";
import EhsRoleForm from "./EhsRoleForm";
import UsersService from "@velocity-ehs/user-utils-module";
import useEhsRoleMutation, {
  EhsRoleActions,
} from "../../../hooks/useEhsRoleMutation";
import { useEhsRoleQuery } from "../../../hooks/useEhsRoleQuery";
import { useEffect, useReducer } from "react";
import { INITIAL_PERM_STATE, PermissionsReducer } from "./PermissionsReducer";
import { useFlags } from "launchdarkly-react-client-sdk";
import { navItems } from "../RoleManagement/NavHelpers";

type Props = {
  mode: Mode;
};

const ManageEhsRole: React.FC<Props> = ({ mode }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const formMethods = useForm<EhsRoleFormFields>({
    mode: "onBlur",
  });

  const { mutations } = useEhsRoleMutation();
  const { id: roleId } = useParams<{ id: string }>();

  const allFlags = useFlags();

  const { ehsRole, assignedPermissions, refetch, isFetched, isFetching } =
    useEhsRoleQuery(UsersService.customer.id, roleId);

  const [permissionsState, dispatch] = useReducer(
    PermissionsReducer,
    INITIAL_PERM_STATE
  );

  const onSave = async () => {
    const formValues = formMethods.getValues();
    formMethods.reset(formValues);
    dispatch({ type: "clean" });
    const isEdit = mode === Mode.EDIT;

    await mutations
      .mutate(
        isEdit ? EhsRoleActions.UPDATE : EhsRoleActions.CREATE,
        formValues,
        roleId,
        permissionsState.permissions
      )
      .then((result) => {
        if (isEdit) return refetch();
        if (result.id) {
          history.push(`/role-management/ehs-roles/${result.id}/edit`);
        }
      })
      .catch((error: any) => {
        /* eslint-disable-next-line no-console */
        console.log(error);
      });
  };

  useEffect(() => {
    if (isFetched) {
      formMethods.reset({
        name: ehsRole.name,
        description: ehsRole.description,
      });
      dispatch({ type: "init", payload: assignedPermissions });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  return (
    <div className="App">
      <CapabilityNav
        capability="Roles Management"
        navItems={navItems()}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <PageHeader
          title={mode === Mode.EDIT ? ehsRole.name : `${t("action_add_role")}`}
          navigationTree={[
            { text: `Accelerate ${t("universal_Home")}`, href: "/" },
            {
              text: t("universal_ehs_roles"),
              href: "/role-management/ehs-roles/",
            },
            {
              text:
                mode === Mode.ADD
                  ? t("universal_Add")
                  : t("roles_edit_role_breadcrumb"),
              href: "",
            },
          ]}
          secondaryButton={{
            label: t("universal_Cancel"),
            onClick: () => history.push("/role-management/ehs-roles"),
          }}
          primaryButton={{
            label: t("universal_Save"),
            type: "submit",
            form: "ehs-role-form",
            disabled: isFetching,
            visible: UsersService.permissions.canManageEhsRoles,
            loading: mutations.isLoading,
          }}
        />
        <VerticalTabs
          tabs={[
            {
              content: (
                <>
                  <EhsRoleForm
                    formMethods={formMethods}
                    onSubmit={onSave}
                    isLoading={isFetching}
                    selected={permissionsState.permissions}
                    dispatch={dispatch}
                  />
                  <UnsavedChangesPrompt
                    isDirty={
                      formMethods.formState.isDirty || permissionsState.isDirty
                    }
                    history={history}
                  />
                </>
              ),
              title: t("universal_details_and_permissions"),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ManageEhsRole;
